import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Mail, AlertTriangle, FileWarning, Calendar, Users, CheckCircle, Clock, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function NotificationSettings() {
  const [settings, setSettings] = useState({
    email_notifications: true,
    budget_alerts: true,
    duplicate_warnings: true,
    extraction_failures: true,
    weekly_summary: false,
    team_activity: true,
    approval_requests: true,
    quiet_hours_start: '',
    quiet_hours_end: ''
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/notifications`, { withCredentials: true });
      setSettings({
        email_notifications: response.data.email_notifications ?? true,
        budget_alerts: response.data.budget_alerts ?? true,
        duplicate_warnings: response.data.duplicate_warnings ?? true,
        extraction_failures: response.data.extraction_failures ?? true,
        weekly_summary: response.data.weekly_summary ?? false,
        team_activity: response.data.team_activity ?? true,
        approval_requests: response.data.approval_requests ?? true,
        quiet_hours_start: response.data.quiet_hours_start || '',
        quiet_hours_end: response.data.quiet_hours_end || ''
      });
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/notifications`, settings, { withCredentials: true });
      toast.success('Notification settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/settings/notifications/test`, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send test notification');
    } finally {
      setTesting(false);
    }
  };

  const NotificationToggle = ({ icon: Icon, title, description, settingKey, disabled = false }) => (
    <div className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
      disabled ? 'opacity-50 bg-slate-50 dark:bg-slate-800' : 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700'
    }`}>
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
          <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <p className="font-medium text-slate-900 dark:text-white">{title}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
        </div>
      </div>
      <button
        onClick={() => !disabled && handleToggle(settingKey)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          settings[settingKey] ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-600'
        }`}
        data-testid={`toggle-${settingKey}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            settings[settingKey] ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-indigo-600" />
            Notification Preferences
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Choose which notifications you want to receive
          </p>
        </div>
        <button
          onClick={handleTestNotification}
          disabled={testing}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          data-testid="test-notification-btn"
        >
          <Send className="w-4 h-4" />
          {testing ? 'Sending...' : 'Test'}
        </button>
      </div>

      {/* Master Toggle */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="w-6 h-6" />
            <div>
              <p className="font-semibold">Email Notifications</p>
              <p className="text-sm text-indigo-100">Master toggle for all email notifications</p>
            </div>
          </div>
          <button
            onClick={() => handleToggle('email_notifications')}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              settings.email_notifications ? 'bg-white/30' : 'bg-black/20'
            }`}
            data-testid="toggle-email-master"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                settings.email_notifications ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Individual Toggles */}
      <div className="space-y-3">
        <NotificationToggle
          icon={AlertTriangle}
          title="Budget Alerts"
          description="Get notified when spending exceeds your threshold"
          settingKey="budget_alerts"
          disabled={!settings.email_notifications}
        />
        
        <NotificationToggle
          icon={FileWarning}
          title="Duplicate Warnings"
          description="Alert when a duplicate invoice is detected"
          settingKey="duplicate_warnings"
          disabled={!settings.email_notifications}
        />
        
        <NotificationToggle
          icon={FileWarning}
          title="Extraction Failures"
          description="Notify when invoice data extraction fails"
          settingKey="extraction_failures"
          disabled={!settings.email_notifications}
        />
        
        <NotificationToggle
          icon={Calendar}
          title="Weekly Summary"
          description="Receive a weekly expense summary every Monday"
          settingKey="weekly_summary"
          disabled={!settings.email_notifications}
        />
        
        <NotificationToggle
          icon={Users}
          title="Team Activity"
          description="Get notified about team member actions"
          settingKey="team_activity"
          disabled={!settings.email_notifications}
        />
        
        <NotificationToggle
          icon={CheckCircle}
          title="Approval Requests"
          description="Notify when approval is needed or granted"
          settingKey="approval_requests"
          disabled={!settings.email_notifications}
        />
      </div>

      {/* Quiet Hours */}
      <div className="p-4 rounded-lg border bg-white dark:bg-slate-800">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-slate-500" />
          <h4 className="font-medium text-slate-900 dark:text-white">Quiet Hours</h4>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Pause notifications during specific hours (optional)
        </p>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">From</label>
            <input
              type="time"
              value={settings.quiet_hours_start}
              onChange={(e) => setSettings(prev => ({ ...prev, quiet_hours_start: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              data-testid="quiet-hours-start"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">To</label>
            <input
              type="time"
              value={settings.quiet_hours_end}
              onChange={(e) => setSettings(prev => ({ ...prev, quiet_hours_end: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              data-testid="quiet-hours-end"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
        data-testid="save-notifications-btn"
      >
        {saving ? 'Saving...' : 'Save Notification Settings'}
      </button>
    </div>
  );
}

export default NotificationSettings;
